import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Edit2, Upload, Check, X, ChevronRight, ShoppingCart, BookOpen, FileText, AlertTriangle, ArrowUp, ArrowDown, Users, Download, Loader2, Lock, CheckCircle, XCircle, MapPin, Truck, Package } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n));

const VirtualStudio = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [selectedTier, setSelectedTier] = useState(null);
  const [track, setTrack] = useState("digital"); // "digital" | "physical"
  const [binding, setBinding] = useState("softcover");
  const [colorPrint, setColorPrint] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [coverFont, setCoverFont] = useState("Cormorant Garamond");
  const [coverFontColor, setCoverFontColor] = useState("#ffffff");
  const [coverTextPosition, setCoverTextPosition] = useState("center");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [hasPaidOrder, setHasPaidOrder] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [shipping, setShipping] = useState({ full_name: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", phone: "" });
  const fileInputRef = useRef(null);

  const FONT_OPTIONS = [
    { value: "Cormorant Garamond", label: "Cormorant Garamond" },
    { value: "Georgia", label: "Georgia" },
    { value: "Playfair Display", label: "Playfair Display" },
    { value: "Lora", label: "Lora" },
    { value: "Merriweather", label: "Merriweather" },
    { value: "Helvetica Neue", label: "Helvetica Neue" },
    { value: "Futura", label: "Futura" },
    { value: "Garamond", label: "Garamond" },
  ];
  const COLOR_PRESETS = ["#ffffff", "#F8F6F1", "#FFD700", "#000000", "#2D3A2D", "#C67B5C", "#E8D5B7", "#1E40AF"];
  const POSITION_MAP = { top: "justify-start pt-10", center: "justify-center", bottom: "justify-end pb-10" };

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const res = await axios.get(`${API}/studio/${bookId}`);
        setData(res.data);
        setSelectedTier(res.data.tier_info.recommended_tier);
        setNewTitle(res.data.book.title);
        setHasPaidOrder(res.data.has_paid_order || false);
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
        else toast.error("Failed to load studio");
      }
      setLoading(false);
    };
    fetchStudio();
  }, [bookId, navigate]);

  useEffect(() => {
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (payment === "success" && sessionId) {
      setPaymentStatus("polling");
      pollPaymentStatus(sessionId, 0);
      setSearchParams({}, { replace: true });
    } else if (payment === "cancelled") {
      setPaymentStatus("cancelled");
      toast.error("Payment was cancelled");
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 8) { setPaymentStatus("failed"); toast.error("Payment status check timed out."); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentStatus("paid");
        setHasPaidOrder(true);
        toast.success("Payment successful! Your PDF is now unlocked.");
        await refresh();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
  };

  const refresh = async () => {
    const res = await axios.get(`${API}/studio/${bookId}`);
    setData(res.data);
    setNewTitle(res.data.book.title);
    setHasPaidOrder(res.data.has_paid_order || false);
  };

  const handleSaveTitle = async () => {
    if (!newTitle.trim()) return;
    try {
      await axios.put(`${API}/studio/${bookId}/customize`, { title: newTitle.trim() });
      await refresh();
      setEditingTitle(false);
      toast.success("Title updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/studio/${bookId}/upload-cover`, formData);
      await refresh();
      toast.success("Cover updated");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const handleOrder = async () => {
    if (!selectedTier) return;
    if (track === "physical") {
      if (!shipping.full_name || !shipping.address_line1 || !shipping.city || !shipping.state || !shipping.pincode || !shipping.phone) {
        toast.error("Please fill in all shipping address fields");
        return;
      }
    }
    setOrdering(true);
    try {
      const payload = {
        book_id: bookId,
        tier: selectedTier,
        track,
        binding: track === "physical" ? binding : undefined,
        color_print: colorPrint,
        origin_url: window.location.origin,
        shipping_address: track === "physical" ? shipping : undefined,
        cover_font: coverFont,
        cover_color: coverFontColor,
        cover_position: coverTextPosition,
      };
      const res = await axios.post(`${API}/orders`, payload);
      if (res.data.checkout_url) { window.location.href = res.data.checkout_url; }
      else { toast.error("Failed to create checkout session"); setOrdering(false); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order failed");
      setOrdering(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const orders = await axios.get(`${API}/orders`);
      const paidOrder = orders.data.orders.find(o => o.book_id === bookId && ["paid", "complete"].includes(o.status));
      if (!paidOrder) { toast.error("No paid order found"); setDownloadingPdf(false); return; }
      const res = await axios.get(`${API}/orders/${paidOrder.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("PDF opened in new tab!");
    } catch (err) {
      toast.error(err.response?.status === 402 ? "Payment required" : "PDF download failed");
    }
    setDownloadingPdf(false);
  };

  const getPrice = () => {
    if (!selectedTier || !data) return 0;
    const t = data.tier_info.tiers[String(selectedTier)];
    if (!t) return 0;
    if (track === "digital") return Math.round(t.digital);
    let price = binding === "hardcover" ? t.physical_hardcover : t.physical_softcover;
    if (colorPrint) price += t.color_markup;
    return Math.round(price);
  };

  if (loading) return (<div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center"><div className="w-10 h-10 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" /></div>);
  if (!data) return (<div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center"><p className="font-sans text-[#7A8A7A]">Book not found</p></div>);

  const { book, chapters, tier_info, attribution } = data;
  const currentTierData = tier_info.tiers[String(selectedTier)] || {};
  const contentFits = currentTierData.fits;

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="virtual-studio">
      <nav className="sticky top-0 z-40 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2" data-testid="home-button">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center"><Leaf className="w-4 h-4 text-white" /></div>
              <span className="font-serif text-lg font-medium text-[#2D3A2D]">My Story</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-[#D4C9B8]" />
            <span className="font-sans text-sm text-[#4A5D4A]">Virtual Studio</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/orders" className="font-sans text-sm text-[#7A9B7E] hover:text-[#4A5D4A] transition-colors" data-testid="orders-link">My Orders</Link>
            <Link to="/app" className="font-sans text-sm text-[#7A8A7A] hover:text-[#4A5D4A] transition-colors">Back to Editor</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Left: Book Preview */}
          <div className="lg:col-span-5">
            <div className="sticky top-20">
              <div className="relative perspective-[1200px] flex justify-center mb-8" data-testid="book-mockup">
                <div className="relative w-[280px] h-[380px]" style={{ transformStyle: "preserve-3d", transform: "rotateY(-15deg) rotateX(2deg)" }}>
                  <div className="absolute left-0 top-0 w-[30px] h-full origin-left" style={{ transform: "rotateY(90deg) translateZ(-15px)", background: "linear-gradient(180deg, #4A5D4A, #2D3A2D)" }} />
                  <div className="absolute inset-0 rounded-r-lg shadow-2xl overflow-hidden border border-[#D4C9B8]/20" style={{ background: book.cover_image ? undefined : "linear-gradient(135deg, #9FB89F, #5A7B5E)", boxShadow: "8px 8px 30px rgba(0,0,0,0.15), -2px 0 10px rgba(0,0,0,0.05)" }}>
                    {book.cover_image && <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />}
                    <div className={`absolute inset-0 flex flex-col items-center p-8 bg-gradient-to-b from-black/20 via-transparent to-black/40 ${POSITION_MAP[coverTextPosition]}`}>
                      <h2 className="text-2xl text-center font-medium drop-shadow-lg leading-tight" style={{ fontFamily: coverFont, color: coverFontColor }}>{book.title}</h2>
                      <div className="mt-3 w-16 h-px" style={{ backgroundColor: coverFontColor + "80" }} />
                      <p className="text-xs mt-3 text-center" style={{ fontFamily: coverFont, color: coverFontColor + "CC" }}>{attribution.author_line}</p>
                      {attribution.type === "collaborative" && (
                        <div className="mt-2 flex items-center gap-1"><Users className="w-3 h-3 text-white/60" /><span className="font-sans text-[10px] text-white/60">{attribution.contributors.length} contributors</span></div>
                      )}
                    </div>
                  </div>
                  <div className="absolute right-0 top-[3px] w-[10px] h-[calc(100%-6px)] rounded-r-sm" style={{ background: "linear-gradient(90deg, #F5F0E8, #E8E0D0, #F5F0E8)", boxShadow: "2px 0 5px rgba(0,0,0,0.1)" }} />
                </div>
              </div>

              {/* PDF Download — locked/unlocked */}
              <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-5 mb-4" data-testid="pdf-download-section">
                {hasPaidOrder ? (
                  <button onClick={handleDownloadPdf} disabled={downloadingPdf} className="w-full py-3.5 bg-[#4A5D4A] text-white rounded-xl font-sans text-sm font-medium flex items-center justify-center gap-2.5 hover:bg-[#2D3A2D] transition-colors disabled:opacity-60" data-testid="download-pdf-btn">
                    {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    {downloadingPdf ? "Generating PDF..." : "Download Your PDF"}
                  </button>
                ) : (
                  <div className="text-center py-2">
                    <div className="w-10 h-10 rounded-full bg-[#D4C9B8]/30 flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-5 h-5 text-[#7A8A7A]" />
                    </div>
                    <p className="font-sans text-sm font-medium text-[#7A8A7A]">PDF Locked</p>
                    <p className="font-sans text-xs text-[#9A9A8A] mt-1">Complete a purchase to unlock your PDF</p>
                  </div>
                )}
              </div>

              <div className="bg-white/60 border border-[#D4C9B8]/40 rounded-2xl p-5" data-testid="attribution-card">
                <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2">Book Credits</p>
                <p className="font-serif text-lg text-[#2D3A2D] font-medium">{attribution.author_line}</p>
                {attribution.attribution && <p className="font-sans text-sm text-[#7A8A7A] mt-2 leading-relaxed">{attribution.attribution}</p>}
              </div>
            </div>
          </div>

          {/* Right: Controls */}
          <div className="lg:col-span-7 space-y-6">

            {/* Payment Status Banners */}
            <AnimatePresence>
              {paymentStatus === "polling" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4" data-testid="payment-polling">
                  <Loader2 className="w-6 h-6 text-amber-600 animate-spin flex-shrink-0" />
                  <div><p className="font-sans text-sm font-medium text-amber-800">Verifying payment...</p><p className="font-sans text-xs text-amber-600 mt-0.5">Please wait while we confirm with Stripe.</p></div>
                </motion.div>
              )}
              {paymentStatus === "paid" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-4" data-testid="payment-success">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div><p className="font-sans text-sm font-medium text-emerald-800">Payment successful!</p><p className="font-sans text-xs text-emerald-600 mt-0.5">Your PDF is ready. Download it from the left panel.</p></div>
                </motion.div>
              )}
              {paymentStatus === "cancelled" && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-4" data-testid="payment-cancelled">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <div><p className="font-sans text-sm font-medium text-red-800">Payment cancelled</p><p className="font-sans text-xs text-red-500 mt-0.5">No charges were made.</p></div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Title Editor */}
            <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="title-editor">
              <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-3">Book Title</p>
              {editingTitle ? (
                <div className="flex items-center gap-3">
                  <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="flex-1 font-serif text-2xl text-[#2D3A2D] bg-[#F8F6F1] border-2 border-[#9FB89F] rounded-xl px-4 py-2 outline-none" autoFocus data-testid="title-input" />
                  <button onClick={handleSaveTitle} className="p-2 bg-[#7A9B7E] text-white rounded-xl"><Check className="w-5 h-5" /></button>
                  <button onClick={() => { setEditingTitle(false); setNewTitle(book.title); }} className="p-2 bg-[#E5D4C1] text-[#6A5A4A] rounded-xl"><X className="w-5 h-5" /></button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl text-[#2D3A2D] font-medium">{book.title}</h2>
                  <button onClick={() => setEditingTitle(true)} className="p-2 hover:bg-[#E5D4C1]/50 rounded-xl transition-colors" data-testid="edit-title-btn"><Edit2 className="w-4 h-4 text-[#7A9B7E]" /></button>
                </div>
              )}
            </div>

            {/* Cover Upload + Text Style (collapsed) */}
            <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="cover-editor">
              <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-3">Cover Photo</p>
              <input type="file" ref={fileInputRef} onChange={handleCoverUpload} accept="image/*" className="hidden" />
              <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-3 px-5 py-3 border border-dashed border-[#D4C9B8] rounded-xl font-sans text-sm text-[#7A8A7A] hover:bg-[#F8F6F1] transition-colors w-full justify-center" data-testid="upload-cover-btn">
                <Upload className="w-4 h-4" />{book.cover_image ? "Replace Cover Photo" : "Upload Cover Photo"}
              </button>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div>
                  <label className="font-sans text-[10px] text-[#7A8A7A] mb-1 block">Font</label>
                  <select value={coverFont} onChange={(e) => setCoverFont(e.target.value)} className="w-full px-2 py-1.5 rounded-lg bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-xs outline-none" data-testid="font-family-select" style={{ fontFamily: coverFont }}>
                    {FONT_OPTIONS.map(f => <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-sans text-[10px] text-[#7A8A7A] mb-1 block">Color</label>
                  <div className="flex items-center gap-1 flex-wrap">
                    {COLOR_PRESETS.slice(0, 5).map(c => (
                      <button key={c} onClick={() => setCoverFontColor(c)} className={`w-5 h-5 rounded-full border-2 transition-all ${coverFontColor === c ? "border-[#7A9B7E] scale-110" : "border-[#D4C9B8]/50"}`} style={{ backgroundColor: c }} />
                    ))}
                    <input type="color" value={coverFontColor} onChange={(e) => setCoverFontColor(e.target.value)} className="w-5 h-5 rounded-full cursor-pointer border border-[#D4C9B8]/50" />
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] text-[#7A8A7A] mb-1 block">Position</label>
                  <div className="flex gap-1">
                    {["top", "center", "bottom"].map(p => (
                      <button key={p} onClick={() => setCoverTextPosition(p)} className={`flex-1 py-1 rounded-lg text-[10px] font-sans transition-all ${coverTextPosition === p ? "bg-[#7A9B7E] text-white" : "bg-[#F8F6F1] text-[#7A8A7A]"}`}>{p}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Content Stats */}
            <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="content-stats">
              <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-4">Content Summary</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center"><p className="font-serif text-3xl text-[#2D3A2D] font-medium">{chapters.length}</p><p className="font-sans text-xs text-[#7A8A7A]">Chapters</p></div>
                <div className="text-center"><p className="font-serif text-3xl text-[#2D3A2D] font-medium">~{tier_info.estimated_pages}</p><p className="font-sans text-xs text-[#7A8A7A]">Est. Pages</p></div>
                <div className="text-center"><p className="font-serif text-3xl text-[#2D3A2D] font-medium">{tier_info.total_images}</p><p className="font-sans text-xs text-[#7A8A7A]">Photos</p></div>
              </div>
            </div>

            {/* === TRACK SELECTION === */}
            <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="track-selector">
              <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-4">Choose Your Track</p>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setTrack("digital")} className={`relative p-5 rounded-xl border-2 transition-all text-left ${track === "digital" ? "border-[#7A9B7E] bg-[#9FB89F]/10 shadow-md" : "border-[#D4C9B8]/30 hover:border-[#D4C9B8]"}`} data-testid="track-digital">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-[#7A9B7E]" />
                    <span className="font-sans text-xs font-semibold uppercase tracking-wider text-[#7A9B7E]">Track A</span>
                  </div>
                  <p className="font-serif text-lg font-medium text-[#2D3A2D]">Digital PDF Only</p>
                  <p className="font-sans text-xs text-[#7A8A7A] mt-1">Instant download after payment</p>
                  {selectedTier && <p className="font-sans text-lg font-medium text-[#4A5D4A] mt-3">₹{formatINR(currentTierData.digital)}</p>}
                </button>
                <button onClick={() => setTrack("physical")} className={`relative p-5 rounded-xl border-2 transition-all text-left ${track === "physical" ? "border-[#C67B5C] bg-[#C67B5C]/5 shadow-md" : "border-[#D4C9B8]/30 hover:border-[#D4C9B8]"}`} data-testid="track-physical">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-5 h-5 text-[#C67B5C]" />
                    <span className="font-sans text-xs font-semibold uppercase tracking-wider text-[#C67B5C]">Track B</span>
                  </div>
                  <p className="font-serif text-lg font-medium text-[#2D3A2D]">Physical Book + PDF</p>
                  <p className="font-sans text-xs text-[#7A8A7A] mt-1">Printed & delivered + digital copy</p>
                  {selectedTier && <p className="font-sans text-lg font-medium text-[#4A5D4A] mt-3">from ₹{formatINR(currentTierData.physical_softcover)}</p>}
                </button>
              </div>
            </div>

            {/* Tier Selection */}
            <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="tier-selector">
              <div className="flex items-center justify-between mb-4">
                <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider">Book Size</p>
                <span className="font-sans text-xs text-[#9FB89F] bg-[#9FB89F]/10 px-2 py-1 rounded-full">Recommended: {tier_info.recommended_tier} pages</span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[25, 50, 100, 150].map((tier) => {
                  const t = tier_info.tiers[String(tier)];
                  const isSelected = selectedTier === tier;
                  const isRecommended = tier === tier_info.recommended_tier;
                  const tierPrice = track === "digital" ? t.digital : (binding === "hardcover" ? t.physical_hardcover : t.physical_softcover);
                  return (
                    <button key={tier} onClick={() => setSelectedTier(tier)} className={`relative p-4 rounded-xl border-2 transition-all text-center ${isSelected ? "border-[#7A9B7E] bg-[#9FB89F]/10 shadow-md" : "border-[#D4C9B8]/30 hover:border-[#D4C9B8]"} ${!t.fits ? "opacity-70" : ""}`} data-testid={`tier-${tier}`}>
                      {isRecommended && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#7A9B7E] text-white text-[9px] font-sans px-2 py-0.5 rounded-full">Best Fit</span>}
                      <p className="font-serif text-2xl text-[#2D3A2D] font-medium">{tier}</p>
                      <p className="font-sans text-xs text-[#7A8A7A]">pages</p>
                      <p className="font-sans text-sm text-[#4A5D4A] font-medium mt-2">₹{formatINR(tierPrice)}</p>
                      {!t.fits && <p className="font-sans text-[10px] text-[#C67B5C] mt-1">Content exceeds</p>}
                    </button>
                  );
                })}
              </div>
              <AnimatePresence>
                {selectedTier && !contentFits && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4 flex items-start gap-3" data-testid="upsell-alert">
                    <AlertTriangle className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-sans text-sm font-medium text-[#92400E]">Content exceeds {selectedTier}-page tier</p>
                      <button onClick={() => { const next = [25, 50, 100, 150].find(t => t > selectedTier && tier_info.tiers[String(t)]?.fits); if (next) setSelectedTier(next); }} className="mt-2 flex items-center gap-1 font-sans text-xs text-[#B45309] font-medium hover:underline"><ArrowUp className="w-3 h-3" /> Upgrade tier</button>
                    </div>
                  </motion.div>
                )}
                {selectedTier && contentFits && selectedTier > tier_info.recommended_tier && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 bg-[#DBEAFE] border border-[#3B82F6]/20 rounded-xl p-4 flex items-start gap-3" data-testid="downgrade-hint">
                    <ArrowDown className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-sans text-sm font-medium text-[#1E40AF]">Save by choosing a smaller tier</p>
                      <button onClick={() => setSelectedTier(tier_info.recommended_tier)} className="mt-2 flex items-center gap-1 font-sans text-xs text-[#2563EB] font-medium hover:underline"><ArrowDown className="w-3 h-3" /> Switch to {tier_info.recommended_tier} pages</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Track B Options: Binding + Color */}
            {track === "physical" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-6">
                <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="binding-selector">
                  <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-4">Binding</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => setBinding("softcover")} className={`p-4 rounded-xl border-2 transition-all text-center ${binding === "softcover" ? "border-[#7A9B7E] bg-[#9FB89F]/10" : "border-[#D4C9B8]/30"}`} data-testid="binding-softcover">
                      <BookOpen className="w-5 h-5 mx-auto mb-2 text-[#7A9B7E]" />
                      <p className="font-sans text-sm font-medium text-[#2D3A2D]">Softcover</p>
                      {selectedTier && <p className="font-sans text-sm text-[#7A9B7E] mt-1">₹{formatINR(currentTierData.physical_softcover)}</p>}
                    </button>
                    <button onClick={() => setBinding("hardcover")} className={`p-4 rounded-xl border-2 transition-all text-center ${binding === "hardcover" ? "border-[#C67B5C] bg-[#C67B5C]/5" : "border-[#D4C9B8]/30"}`} data-testid="binding-hardcover">
                      <BookOpen className="w-5 h-5 mx-auto mb-2 text-[#C67B5C]" />
                      <p className="font-sans text-sm font-medium text-[#2D3A2D]">Hardcover</p>
                      {selectedTier && <p className="font-sans text-sm text-[#C67B5C] mt-1">₹{formatINR(currentTierData.physical_hardcover)}</p>}
                    </button>
                  </div>
                  <div className="mt-4 flex items-center justify-between bg-[#F8F6F1] rounded-xl p-4">
                    <div><p className="font-sans text-sm text-[#2D3A2D]">Full Color Pages</p><p className="font-sans text-xs text-[#7A8A7A]">+₹{selectedTier ? formatINR(currentTierData.color_markup) : 0}</p></div>
                    <button onClick={() => setColorPrint(!colorPrint)} className={`relative w-12 h-6 rounded-full transition-colors ${colorPrint ? "bg-[#7A9B7E]" : "bg-[#D4C9B8]"}`} data-testid="color-toggle">
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${colorPrint ? "translate-x-6" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white/70 border border-[#D4C9B8]/40 rounded-2xl p-6" data-testid="shipping-form">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-[#C67B5C]" />
                    <p className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider">Shipping Address</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={shipping.full_name} onChange={(e) => setShipping(p => ({...p, full_name: e.target.value}))} placeholder="Full Name *" className="col-span-2 px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-name" />
                    <input value={shipping.address_line1} onChange={(e) => setShipping(p => ({...p, address_line1: e.target.value}))} placeholder="Address Line 1 *" className="col-span-2 px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-addr1" />
                    <input value={shipping.address_line2} onChange={(e) => setShipping(p => ({...p, address_line2: e.target.value}))} placeholder="Address Line 2" className="col-span-2 px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-addr2" />
                    <input value={shipping.city} onChange={(e) => setShipping(p => ({...p, city: e.target.value}))} placeholder="City *" className="px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-city" />
                    <input value={shipping.state} onChange={(e) => setShipping(p => ({...p, state: e.target.value}))} placeholder="State *" className="px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-state" />
                    <input value={shipping.pincode} onChange={(e) => setShipping(p => ({...p, pincode: e.target.value}))} placeholder="PIN Code *" className="px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-pincode" />
                    <input value={shipping.phone} onChange={(e) => setShipping(p => ({...p, phone: e.target.value}))} placeholder="Phone *" className="px-4 py-2.5 rounded-xl bg-[#F8F6F1] border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="ship-phone" />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Order Summary */}
            <div className="bg-[#2D3A2D] rounded-2xl p-6 text-white" data-testid="order-summary">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-sans text-xs text-white/60 uppercase tracking-wider">Total</p>
                  <p className="font-serif text-4xl font-medium">₹{formatINR(getPrice())}</p>
                </div>
                <div className="text-right font-sans text-xs text-white/60 space-y-0.5">
                  <p>{selectedTier} pages</p>
                  <p>{track === "digital" ? "Digital PDF" : `${binding === "hardcover" ? "Hardcover" : "Softcover"} Print + PDF`}</p>
                  {track === "physical" && colorPrint && <p>+ Color pages</p>}
                </div>
              </div>
              <button onClick={handleOrder} disabled={ordering || paymentStatus === "polling"} className="w-full py-4 bg-[#C67B5C] text-white rounded-xl font-sans font-medium text-base flex items-center justify-center gap-3 hover:bg-[#B66B4C] transition-colors disabled:opacity-60" data-testid="place-order-btn">
                <ShoppingCart className="w-5 h-5" />
                {ordering ? "Redirecting to payment..." : `Pay ₹${formatINR(getPrice())} with Stripe`}
              </button>
              <p className="font-sans text-[10px] text-white/40 text-center mt-3">Secure payment via Stripe. {track === "physical" ? "Book will be printed & shipped after payment." : "PDF available instantly after payment."}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualStudio;
